import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

const DatePicker = ({ 
  selectedDate, 
  onChange, 
  className = '',
  dateFormat = 'date-object' // 'date-object' | 'iso-string'
}) => {
  // Handle both Date objects and ISO string dates
  const parseDate = (date) => {
    if (!date) return null;
    if (typeof date === 'string') {
      // For ISO date strings (YYYY-MM-DD), parse as local date not UTC
      return new Date(date + 'T00:00:00');
    }
    return date;
  };

  const dateValue = parseDate(selectedDate);
  const [showCalendar, setShowCalendar] = useState(false);
  const [viewDate, setViewDate] = useState(dateValue || new Date());

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (date) => {
    if (!date) return 'Select Date';
    const dateObj = parseDate(date);
    return dateObj.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleDateSelect = (day) => {
    const newDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    // Return format based on dateFormat prop
    if (dateFormat === 'iso-string') {
      // Format as YYYY-MM-DD using local date components to avoid timezone issues
      const year = newDate.getFullYear();
      const month = String(newDate.getMonth() + 1).padStart(2, '0');
      const dayStr = String(newDate.getDate()).padStart(2, '0');
      onChange(`${year}-${month}-${dayStr}`);
    } else {
      onChange(newDate);
    }
    setShowCalendar(false);
  };

  const handleMonthChange = (direction) => {
    const newDate = new Date(viewDate);
    newDate.setMonth(viewDate.getMonth() + direction);
    setViewDate(newDate);
  };

  const handleQuickSelect = (days) => {
    const newDate = new Date();
    newDate.setDate(newDate.getDate() + days);
    // Return format based on dateFormat prop
    if (dateFormat === 'iso-string') {
      // Format as YYYY-MM-DD using local date components to avoid timezone issues
      const year = newDate.getFullYear();
      const month = String(newDate.getMonth() + 1).padStart(2, '0');
      const day = String(newDate.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      console.log('🗓️ [DatePicker] Quick select:', days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : 'Yesterday', '→', dateString);
      onChange(dateString);
    } else {
      onChange(newDate);
    }
    setShowCalendar(false);
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(viewDate);
    const firstDay = getFirstDayOfMonth(viewDate);
    const days = [];
    
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} />);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const isSelected = dateValue && 
        dateValue.getDate() === day &&
        dateValue.getMonth() === viewDate.getMonth() &&
        dateValue.getFullYear() === viewDate.getFullYear();
      
      const isToday = new Date().toDateString() === 
        new Date(viewDate.getFullYear(), viewDate.getMonth(), day).toDateString();
      
      days.push(
        <motion.button
          key={day}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => handleDateSelect(day)}
          className={`
            p-2 rounded-lg text-sm font-medium transition-all
            ${isSelected 
              ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white' 
              : isToday
                ? 'bg-gray-700 text-blue-400 border border-blue-400/50'
                : 'hover:bg-gray-700 text-gray-300'
            }
          `}
        >
          {day}
        </motion.button>
      );
    }
    
    return days;
  };

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setShowCalendar(!showCalendar)}
        className="w-full px-4 py-2 bg-gray-800/50 hover:bg-gray-700/50 border border-gray-700/50 rounded-lg flex items-center gap-2 text-left transition-all focus:outline-none focus:border-blue-500/50"
      >
        <Calendar className="w-4 h-4 text-gray-400" />
        <span className="text-white">{formatDate(dateValue)}</span>
      </button>

      <AnimatePresence>
        {showCalendar && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-[9999] mt-2 bg-gray-800 border border-gray-700 rounded-lg shadow-xl p-4 w-80"
          >
            <div className="flex justify-between items-center mb-4">
              <button
                onClick={() => handleMonthChange(-1)}
                className="p-1 hover:bg-gray-700 rounded transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-400" />
              </button>
              <h3 className="text-white font-medium">
                {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
              </h3>
              <button
                onClick={() => handleMonthChange(1)}
                className="p-1 hover:bg-gray-700 rounded transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-4">
              <button
                onClick={() => handleQuickSelect(-1)}
                className="px-3 py-1 bg-gray-700/50 hover:bg-gray-700 rounded text-sm text-gray-300 transition-colors"
              >
                Yesterday
              </button>
              <button
                onClick={() => handleQuickSelect(0)}
                className="px-3 py-1 bg-gray-700/50 hover:bg-gray-700 rounded text-sm text-gray-300 transition-colors"
              >
                Today
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('🚨 [DatePicker] Tomorrow button CLICKED!');
                  handleQuickSelect(1);
                }}
                className="px-3 py-1 bg-gray-700/50 hover:bg-gray-700 rounded text-sm text-gray-300 transition-colors"
              >
                Tomorrow
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                <div key={day} className="text-center text-xs text-gray-500 font-medium p-2">
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {renderCalendarDays()}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DatePicker;